import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InterviewerCard as InterviewerCardType } from '../../types/interviewer';
import { getFullImageUrl } from '../../utils/imageUtils';
import { DEFAULT_AVATAR } from '../../utils/defaultAvatar';

interface InterviewerCardProps {
  interviewer: InterviewerCardType;
  onSelect: (interviewerId: string) => void;
}

export function InterviewerCard({ interviewer, onSelect }: InterviewerCardProps) {
  const { t } = useTranslation();
  const rating = Math.min(5, Math.max(0, Math.round(interviewer.rating)));

  return (
    <article className="group flex h-full flex-col rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card transition hover:border-stitch-primary/30 hover:shadow-stitch-pop">
      <div className="mb-4 flex items-start gap-4">
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full border-2 border-stitch-border bg-stitch-surface-2 ring-2 ring-stitch-primary/10">
          <img
            src={getFullImageUrl(interviewer.profilePictureUrl) || DEFAULT_AVATAR}
            alt=""
            className="h-full w-full object-cover"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-bold text-stitch-fg">{interviewer.profileName}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-0.5" aria-label={`Rating ${rating} of 5`}>
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={`material-symbols-outlined text-[1.125rem] leading-none ${
                    i < rating ? 'text-stitch-warning' : 'text-stitch-border'
                  }`}
                  style={{ fontVariationSettings: i < rating ? "'FILL' 1, 'wght' 500" : "'wght' 400" }}
                >
                  star
                </span>
              ))}
            </div>
            <span className="text-xs text-stitch-muted">
              ({interviewer.totalInterviews} {t('interviewers.interviews', 'interviews')})
            </span>
          </div>
        </div>
      </div>

      <div className="mb-4 flex-1 space-y-2 text-sm">
        <div className="flex gap-2">
          <span className="material-symbols-outlined shrink-0 text-base text-stitch-muted">psychology</span>
          <div>
            <span className="font-semibold text-stitch-fg-soft">{t('interviewers.specialty', 'Specialty')}</span>
            <p className="text-stitch-muted">{interviewer.specialty}</p>
          </div>
        </div>
        {interviewer.company ? (
          <div className="flex gap-2">
            <span className="material-symbols-outlined shrink-0 text-base text-stitch-muted">apartment</span>
            <div>
              <span className="font-semibold text-stitch-fg-soft">{t('bookings.company', 'Company')}</span>
              <p className="text-stitch-muted">{interviewer.company}</p>
            </div>
          </div>
        ) : null}
        <div className="flex gap-2">
          <span className="material-symbols-outlined shrink-0 text-base text-stitch-muted">translate</span>
          <div>
            <span className="font-semibold text-stitch-fg-soft">{t('interviewers.languages', 'Languages')}</span>
            <p className="text-stitch-muted">{interviewer.languages.join(', ')}</p>
          </div>
        </div>
      </div>

      {interviewer.nextAvailableSlot && (
        <div className="mb-4 rounded-stitch-lg border border-stitch-success/30 bg-stitch-success-soft/60 px-3 py-2 dark:bg-stitch-success-soft/20">
          <div className="flex items-center gap-2 text-sm text-stitch-success">
            <span className="material-symbols-outlined text-base">event_available</span>
            <span>
              <span className="font-semibold">{t('interviewers.nextAvailable', 'Next available')}: </span>
              {new Date(interviewer.nextAvailableSlot).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => onSelect(interviewer.id)}
        className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-stitch-lg bg-stitch-primary py-2.5 text-sm font-semibold text-white shadow-md shadow-stitch-primary/20 transition hover:bg-stitch-primary-hover active:scale-[0.99]"
      >
        <span className="material-symbols-outlined text-lg">calendar_add_on</span>
        {t('interviewers.select', 'Book interview')}
      </button>
    </article>
  );
}
